import React, { useState } from "react";
import AsyncSelect from "react-select/async";
import { debounce } from "underscore";
import { getActs, getRules, getActivities } from "../../backend/masters";
import { DEFAULT_OPTIONS_PAYLOAD } from "./Table";
import { DEBOUNCE_TIME } from "../../utils/constants";
import { activityOptionLabel, ruleOptionLabel } from "../pages/Masters/Mappings/MappingDetails";
import "./AuditLocationFiltersV2.css";

interface AuditLocationFiltersV2Props {
  onApply: (filters: Record<string, any>) => void;
  onClose: () => void;
  initialFilters?: Record<string, any>;
}

const drawerSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: "44px",
    borderColor: state.isFocused ? "#2965ad" : "#D9D9D9",
    borderRadius: "8px",
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "14px",
    fontWeight: 400,
    boxShadow: "none",
    "&:hover": { borderColor: "#2965ad" },
  }),
  placeholder: (base: any) => ({
    ...base,
    color: "#757575",
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "14px",
    fontWeight: 400,
  }),
  singleValue: (base: any) => ({
    ...base,
    color: "#1E1E1E",
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "14px",
    fontWeight: 400,
  }),
  input: (base: any) => ({
    ...base,
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "14px",
    fontWeight: 400,
  }),
  option: (base: any, state: any) => ({
    ...base,
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "14px",
    fontWeight: state.isSelected ? 600 : 400,
  }),
};

function drawerActLabel(opt: any, { context }: any) {
  const { label, act = {} } = opt;
  if (context === 'menu') {
    return (
      <div className="d-flex flex-column">
        <div>
          {label}{act.establishment_type ? ` - ` : ''}
          {act.establishment_type && <span className="fw-bold ms-1">{act.establishment_type}</span>}
        </div>
      </div>
    );
  }
  return (
    <div style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
      {label}{act.establishment_type ? ` - ${act.establishment_type}` : ''}
    </div>
  );
}

function drawerActivityLabel(opt: any, { context }: any) {
  const { label, activity } = opt;
  if (context === 'menu') return activityOptionLabel(opt);
  return (
    <div style={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
      <div style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{label}</div>
      {Boolean(activity) && (
        <>
          <div style={{ fontSize: 12, fontWeight: 600 }}>Type: {activity.activity_type}</div>
          <div style={{ fontSize: 12, fontWeight: 600 }}>{activity.periodicity} | {activity.calendar_type}</div>
        </>
      )}
    </div>
  );
}

function drawerRuleLabel(opt: any, { context }: any) {
  const { label, rule = {} } = opt;
  if (context === 'menu') return ruleOptionLabel(opt);
  return (
    <div style={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
      <div style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{label}</div>
      {(rule.section_no || rule.rule_no) && (
        <div style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
          {rule.section_no && <><span style={{ fontStyle: 'italic' }}>Section No.</span><span style={{ fontWeight: 600 }}>{rule.section_no}</span></>}
          {rule.section_no && rule.rule_no && <span style={{ margin: '0 4px' }}>|</span>}
          {rule.rule_no && <><span style={{ fontStyle: 'italic' }}>Rule No.</span><span style={{ fontWeight: 600 }}>{rule.rule_no}</span></>}
        </div>
      )}
    </div>
  );
}

function AuditLocationFiltersV2({
  onApply,
  onClose,
  initialFilters,
}: AuditLocationFiltersV2Props) {
  const [act, setAct] = useState<any>(
    (initialFilters && initialFilters._act) || null
  );
  const [activity, setActivity] = useState<any>(
    (initialFilters && initialFilters._activity) || null
  );
  const [rule, setRule] = useState<any>(
    (initialFilters && initialFilters._rule) || null
  );

  const [actOptions, setActOptions] = useState<any[]>([]);
  const [ruleOptions, setRuleOptions] = useState<any[]>([]);
  const [activityOptions, setActivityOptions] = useState<any[]>([]);

  const loadActs = debounce((keyword: string, callback: any) => {
    getActs({
      ...DEFAULT_OPTIONS_PAYLOAD,
      filters: { name_cont: keyword },
    }).then((response: any) => {
      const list = (((response || {}).data || {}).items) || [];
      const options = list.map((item: any) => ({
        value: item.id,
        label: item.name,
        act: item,
      }));
      setActOptions(options);
      callback(options);
    });
  }, DEBOUNCE_TIME);

  const loadRules = debounce((keyword: string, callback: any) => {
    getRules({
      ...DEFAULT_OPTIONS_PAYLOAD,
      page: 1,
      per: 100,
      filters: { name_or_unique_identifier_cont: keyword },
    }).then((response: any) => {
      const list = (((response || {}).data || {}).items) || [];
      const options = list.map((item: any) => ({
        value: item.id,
        label: item.name,
        rule: item,
      }));
      setRuleOptions(options);
      callback(options);
    });
  }, DEBOUNCE_TIME);

  const loadActivities = debounce((keyword: string, callback: any) => {
    getActivities({
      ...DEFAULT_OPTIONS_PAYLOAD,
      filters: { name_cont: keyword },
    }).then((response: any) => {
      const list = (((response || {}).data || {}).items) || [];
      const options = list.map((item: any) => ({
        value: item.id,
        label: item.name,
        activity: item,
      }));
      setActivityOptions(options);
      callback(options);
    });
  }, DEBOUNCE_TIME);

  function handleApply() {
    const filters: Record<string, any> = {};
    if (act) {
      filters.act_id_eq = act.value;
      filters._act = act;
    }
    if (activity) {
      filters.activity_id_eq = activity.value;
      filters._activity = activity;
    }
    if (rule) {
      filters.rule_id_eq = rule.value;
      filters._rule = rule;
    }
    onApply(filters);
    onClose();
  }

  function handleClear() {
    setAct(null);
    setRule(null);
    setActivity(null);
  }

  return (
    <>
      <div className="la-drawer-backdrop open" />
      <div className="la-drawer open">
        <div className="la-drawer-head">
          <span className="la-drawer-title">More Filters</span>
          <button type="button" className="la-drawer-close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="la-drawer-body">
          <div className="la-drawer-field">
            <label>Act</label>
            <AsyncSelect
              value={act}
              onChange={(opt: any) => setAct(opt)}
              loadOptions={loadActs}
              defaultOptions={actOptions}
              formatOptionLabel={drawerActLabel}
              isClearable
              placeholder="Search Act..."
              classNamePrefix="la-select"
              noOptionsMessage={({ inputValue }: any) => inputValue ? "No results found" : "Start typing to search..."}
            />
            <span className="la-drawer-field-hint">Hint: Start typing Act to see results.</span>
          </div>
          <div className="la-drawer-field">
            <label>Rule</label>
            <AsyncSelect
              value={rule}
              onChange={(opt: any) => setRule(opt)}
              loadOptions={loadRules}
              defaultOptions={ruleOptions}
              formatOptionLabel={drawerRuleLabel}
              isClearable
              placeholder="Search Rule..."
              classNamePrefix="la-select"
              noOptionsMessage={({ inputValue }: any) => inputValue ? "No results found" : "Start typing to search..."}
            />
            <span className="la-drawer-field-hint">Hint: Start typing Rule to see results.</span>
          </div>
          <div className="la-drawer-field">
            <label>Activity</label>
            <AsyncSelect
              value={activity}
              onChange={(opt: any) => setActivity(opt)}
              loadOptions={loadActivities}
              defaultOptions={activityOptions}
              formatOptionLabel={drawerActivityLabel}
              isClearable
              placeholder="Search Activity..."
              classNamePrefix="la-select"
              noOptionsMessage={({ inputValue }: any) => inputValue ? "No results found" : "Start typing to search..."}
            />
            <span className="la-drawer-field-hint">Hint: Start typing Activity to see results.</span>
          </div>
        </div>
        <div className="la-drawer-foot">
          <button type="button" className="la-drawer-clear-btn" onClick={handleClear}>
            Clear
          </button>
          <button
            type="button"
            className="la-drawer-apply-btn"
            onClick={handleApply}
            disabled={!act && !activity && !rule}
          >
            Apply
          </button>
        </div>
      </div>
    </>
  );
}

export default AuditLocationFiltersV2;
